import { useCallback, useEffect, useState } from "react";

export interface Region {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface RegionCaptureProps {
  /** Zone sélectionnée au cliquer-glisser (coordonnées viewport). */
  onCapture: (region: Region) => void;
  /** « Tout capturer » — capture du viewport entier. */
  onCaptureFull: () => void;
  /** Échap ou « Continuer sans capture ». */
  onSkip: () => void;
}

const MIN_SIZE = 12;

function toRegion(start: { x: number; y: number }, end: { x: number; y: number }): Region {
  return {
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
    width: Math.abs(end.x - start.x),
    height: Math.abs(end.y - start.y),
  };
}

/**
 * Overlay de capture de zone, façon outil de capture de l'OS : l'utilisateur
 * dessine un rectangle au cliquer-glisser, la zone est capturée au relâchement.
 */
export function RegionCapture({ onCapture, onCaptureFull, onSkip }: RegionCaptureProps) {
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [current, setCurrent] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onSkip();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onSkip]);

  const handleDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setStart({ x: e.clientX, y: e.clientY });
    setCurrent({ x: e.clientX, y: e.clientY });
  }, []);

  const handleMove = useCallback(
    (e: React.PointerEvent) => {
      if (!start) return;
      setCurrent({ x: e.clientX, y: e.clientY });
    },
    [start],
  );

  const handleUp = useCallback(
    (e: React.PointerEvent) => {
      if (!start) return;
      const region = toRegion(start, { x: e.clientX, y: e.clientY });
      setStart(null);
      setCurrent(null);
      // Un simple clic (pas de glisser) n'est pas une sélection : on ignore.
      if (region.width >= MIN_SIZE && region.height >= MIN_SIZE) onCapture(region);
    },
    [start, onCapture],
  );

  const selection = start && current ? toRegion(start, current) : null;

  return (
    <>
      <div
        className={"bw-capture-overlay" + (selection ? " bw-capture-overlay-active" : "")}
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
      >
        {selection && (
          <div
            className="bw-capture-selection"
            style={{
              left: selection.x,
              top: selection.y,
              width: selection.width,
              height: selection.height,
            }}
          >
            <span className="bw-capture-size">
              {Math.round(selection.width)} × {Math.round(selection.height)}
            </span>
          </div>
        )}
      </div>
      {!selection && (
        <div className="bw-inspect-toolbar">
          <span>Sélectionnez la zone à capturer — Échap pour passer</span>
          <button type="button" className="bw-link-btn" onClick={onCaptureFull}>
            Tout capturer
          </button>
          <button type="button" className="bw-link-btn bw-link-btn-muted" onClick={onSkip}>
            Sans capture
          </button>
        </div>
      )}
    </>
  );
}
